import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ApiError,
  getFund,
  listFundPrices,
  type AllocationBucketRow,
  type CalendarReturnRow,
  type FundDetail,
  type FundPriceSnapshot,
  type HoldingRow,
  type TrailingReturnRow,
} from '../api';
import { FundPriceChart } from '../components/FundPriceChart';

export default function FundDetailPage() {
  const { accountId = '', id = '' } = useParams<{ accountId: string; id: string }>();
  const [detail, setDetail] = useState<FundDetail | null>(null);
  const [prices, setPrices] = useState<FundPriceSnapshot[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fundsHref = `/accounts/${encodeURIComponent(accountId)}/funds`;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // Fetch detail + history in parallel — the chart shouldn't block the
    // page render but they're cheap to ask for at the same time.
    Promise.all([getFund(accountId, id), listFundPrices(accountId, id)])
      .then(([d, p]) => {
        if (cancelled) return;
        setDetail(d);
        setPrices(p.prices);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setError('Fund not found.');
        } else if (err instanceof ApiError && err.status === 403) {
          setError('You do not have access to this household.');
        } else {
          setError(err instanceof Error ? err.message : 'Failed to load');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, id]);

  if (loading) return <p>Loading…</p>;
  if (error)
    return (
      <div>
        <p style={{ color: '#b00' }}>{error}</p>
        <p>
          <Link to={fundsHref}>← back to funds</Link>
        </p>
      </div>
    );
  if (!detail) return null;

  const { fund, latest_price, latest_allocation } = detail;

  return (
    <div>
      <p style={{ marginTop: 0 }}>
        <Link to={fundsHref}>← back to funds</Link>
      </p>
      <h2 style={{ marginBottom: '0.25rem' }}>{fund.name || fund.id}</h2>
      <p style={{ color: '#666', margin: '0 0 1rem' }}>
        {fund.category}
        {fund.sedol && <span> · SEDOL {fund.sedol}</span>}
        {fund.isin && <span> · ISIN {fund.isin}</span>}
      </p>

      <Section title="Snapshot">
        <KeyValueGrid
          rows={[
            latest_price?.nav_pence != null
              ? ['NAV', formatPrice(latest_price.nav_pence, latest_price.currency)]
              : null,
            latest_price?.day_change_pct != null
              ? ['Day change', `${latest_price.day_change_pct.toFixed(2)}%`]
              : null,
            latest_price?.date ? ['As at', latest_price.date] : null,
            fund.ongoing_charge_pct > 0
              ? ['Ongoing charge', `${fund.ongoing_charge_pct.toFixed(2)}%`]
              : null,
            fund.performance_fee_pct > 0
              ? ['Performance fee', `${fund.performance_fee_pct.toFixed(2)}%`]
              : null,
            fund.initial_charge_pct > 0
              ? ['Initial charge', `${fund.initial_charge_pct.toFixed(2)}%`]
              : null,
            latest_allocation?.yield_pct
              ? ['Yield', `${latest_allocation.yield_pct.toFixed(2)}%`]
              : null,
            latest_allocation?.total_net_assets_mil
              ? ['Total net assets', `£${latest_allocation.total_net_assets_mil.toFixed(1)}m`]
              : null,
            fund.benchmark ? ['Benchmark', fund.benchmark] : null,
            fund.manager_name ? ['Manager', fund.manager_name] : null,
            fund.inception_date ? ['Inception', fund.inception_date] : null,
          ]}
        />
      </Section>

      {prices && (
        <Section title="Price history">
          <p style={{ color: '#666', margin: '0 0 0.5rem', fontSize: '0.85rem' }}>
            {historyLabel(prices)}
          </p>
          <FundPriceChart prices={prices} />
        </Section>
      )}

      {latest_allocation?.trailing_returns?.length ? (
        <Section title="Trailing returns (%)">
          <TrailingTable rows={latest_allocation.trailing_returns} />
        </Section>
      ) : null}

      {latest_allocation?.calendar_returns?.length ? (
        <Section title="Calendar-year returns (%)">
          <CalendarTable rows={latest_allocation.calendar_returns} />
        </Section>
      ) : null}

      {latest_allocation?.top_regions?.length ? (
        <Section title="Top regions">
          <BucketTable rows={latest_allocation.top_regions} />
        </Section>
      ) : null}

      {latest_allocation?.top_sectors?.length ? (
        <Section title="Top sectors">
          <BucketTable rows={latest_allocation.top_sectors} />
        </Section>
      ) : null}

      {latest_allocation?.top_holdings?.length ? (
        <Section title="Top holdings">
          <HoldingsTable rows={latest_allocation.top_holdings} />
        </Section>
      ) : null}

      {fund.source_url && (
        <p style={{ color: '#888', fontSize: '0.85rem', marginTop: '2rem' }}>
          Source:{' '}
          <a href={fund.source_url} target="_blank" rel="noreferrer">
            {fund.source_url}
          </a>
        </p>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginTop: '1.5rem' }}>
      <h3 style={{ marginBottom: '0.5rem' }}>{title}</h3>
      {children}
    </section>
  );
}

function KeyValueGrid({ rows }: { rows: Array<[string, string] | null> }) {
  const filtered = rows.filter((r): r is [string, string] => r !== null);
  if (filtered.length === 0) return <p style={{ color: '#888' }}>No data.</p>;
  return (
    <dl
      style={{
        display: 'grid',
        gridTemplateColumns: 'max-content 1fr',
        rowGap: '0.35rem',
        columnGap: '1rem',
        margin: 0,
      }}
    >
      {filtered.map(([k, v]) => (
        <Row key={k} k={k} v={v} />
      ))}
    </dl>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <>
      <dt style={{ color: '#666' }}>{k}</dt>
      <dd style={{ margin: 0 }}>{v}</dd>
    </>
  );
}

function BucketTable({ rows }: { rows: AllocationBucketRow[] }) {
  return (
    <table style={tableStyle}>
      <tbody>
        {rows.map((r) => (
          <tr key={r.bucket}>
            <td style={cellStyle}>{r.bucket}</td>
            <td style={{ ...cellStyle, textAlign: 'right' }}>{r.pct.toFixed(2)}%</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function HoldingsTable({ rows }: { rows: HoldingRow[] }) {
  return (
    <table style={tableStyle}>
      <tbody>
        {rows.map((r, i) => (
          <tr key={`${r.name}-${i}`}>
            <td style={cellStyle}>{r.name}</td>
            <td style={{ ...cellStyle, color: '#666' }}>{r.sector}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TrailingTable({ rows }: { rows: TrailingReturnRow[] }) {
  return (
    <table style={tableStyle}>
      <thead>
        <tr>
          <th style={headStyle}>Period</th>
          <th style={{ ...headStyle, textAlign: 'right' }}>Fund</th>
          <th style={{ ...headStyle, textAlign: 'right' }}>Category</th>
          <th style={{ ...headStyle, textAlign: 'right' }}>Benchmark</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.period}>
            <td style={cellStyle}>{r.period}</td>
            <td style={{ ...cellStyle, textAlign: 'right' }}>{fmtNum(r.fund)}</td>
            <td style={{ ...cellStyle, textAlign: 'right' }}>{fmtNum(r.category)}</td>
            <td style={{ ...cellStyle, textAlign: 'right' }}>{fmtNum(r.benchmark)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CalendarTable({ rows }: { rows: CalendarReturnRow[] }) {
  return (
    <table style={tableStyle}>
      <thead>
        <tr>
          <th style={headStyle}>Year</th>
          <th style={{ ...headStyle, textAlign: 'right' }}>Fund</th>
          <th style={{ ...headStyle, textAlign: 'right' }}>Category</th>
          <th style={{ ...headStyle, textAlign: 'right' }}>Benchmark</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.year}>
            <td style={cellStyle}>{r.year}</td>
            <td style={{ ...cellStyle, textAlign: 'right' }}>{fmtNum(r.fund)}</td>
            <td style={{ ...cellStyle, textAlign: 'right' }}>{fmtNum(r.category)}</td>
            <td style={{ ...cellStyle, textAlign: 'right' }}>{fmtNum(r.benchmark)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const tableStyle: React.CSSProperties = {
  borderCollapse: 'collapse',
  width: '100%',
  fontSize: '0.9rem',
};

const cellStyle: React.CSSProperties = {
  padding: '0.35rem 0.5rem',
  borderBottom: '1px solid #eee',
};

const headStyle: React.CSSProperties = {
  ...cellStyle,
  textAlign: 'left',
  color: '#666',
  fontWeight: 500,
};

function fmtNum(n: number): string {
  if (n === 0) return '—';
  return n.toFixed(2);
}

function historyLabel(prices: FundPriceSnapshot[]): string {
  if (prices.length === 0) return 'No history yet.';
  const oldest = prices[0].date;
  const newest = prices[prices.length - 1].date;
  return `${prices.length.toLocaleString('en-GB')} daily NAVs · ${oldest} → ${newest}`;
}

// gbpPrice keeps NAV precision (II shows 4 dp like 291.3548) — separate
// from monetary totals where 2 dp is right.
const gbpPrice = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

// formatPrice keeps GBX-quoted unit trusts in their native pence form
// ("232.8140p") and renders GBP-quoted funds as pounds ("£291.3548"). Mirrors
// the helper in Holdings.tsx so a fund's NAV reads the same on both pages.
function formatPrice(pence: number, currency?: string): string {
  if (currency === 'GBX') {
    return `${pence.toLocaleString('en-GB', { maximumFractionDigits: 4 })}p`;
  }
  if (currency === 'GBP') {
    return gbpPrice.format(pence / 100);
  }
  if (pence < 100) {
    return `${pence.toLocaleString('en-GB', { maximumFractionDigits: 4 })}p`;
  }
  return gbpPrice.format(pence / 100);
}
